import React, { useEffect, useState, useMemo } from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, Select, message, Space, Card, Divider, Collapse, Row, Col, Statistic, Grid, Popconfirm } from 'antd';
import { PlusOutlined, DeleteOutlined, EyeOutlined, EditOutlined, SearchOutlined, FilterOutlined, ClearOutlined, SwapOutlined } from '@ant-design/icons';
import { urunlerAPI, urunRecetelerAPI, malzemelerAPI, listeFiyatlariAPI, malzemeBirimDonusumleriAPI } from '../services/api';
import { useLookups } from '../hooks/useLookups';
import UrunDetayModal from '../components/UrunDetayModal';
import BirimDonusumModal from '../components/BirimDonusumModal';
import ReceteModal from '../components/ReceteModal';
import { formatPara, formatSayi, paraInputProps } from '../utils/format';
import { bosSecenekliOptions, secimeUyuyor } from '../utils/filtre';

function Urunler() {
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md; // < 768
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [receteModalOpen, setReceteModalOpen] = useState(false);
  const [selectedUrun, setSelectedUrun] = useState(null);
  const [malzemeler, setMalzemeler] = useState([]);
  const [form] = Form.useForm();
  const [malzemeArama, setMalzemeArama] = useState('');
  const [aramaMetni, setAramaMetni] = useState('');
  const [filtreAdIds, setFiltreAdIds] = useState([]);
  const [filtreTipIds, setFiltreTipIds] = useState([]);
  const [filtreFiyatOp, setFiltreFiyatOp] = useState(null);
  const [filtreFiyatDeger, setFiltreFiyatDeger] = useState(null);
  const [filtreMaliyetOp, setFiltreMaliyetOp] = useState(null);
  const [filtreMaliyetDeger, setFiltreMaliyetDeger] = useState(null);
  const [filtreAcik, setFiltreAcik] = useState([]);
  const [malzemeModalOpen, setMalzemeModalOpen] = useState(false);
  const [malzemeForm] = Form.useForm();
  const [detayOpen, setDetayOpen] = useState(false);
  const [detayUrun, setDetayUrun] = useState(null);
  const [donusumOpen, setDonusumOpen] = useState(false);
  const [donusumMalzeme, setDonusumMalzeme] = useState(null);

  const {
    birimler: birimlerRaw,
    birimOptions: birimler,
    urunTipiOptions: urunTipleri,
    malzemeTipiOptions: malzemeTipleri,
  } = useLookups();

  const porsiyonBirimId = birimlerRaw.find(b => b.ad === 'porsiyon')?.id;

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await urunlerAPI.getAll();
      setData(res.data);
    } catch (err) {
      message.error('Ürünler yüklenemedi: ' + (err.response?.data?.hata || err.message));
    } finally {
      setLoading(false);
    }
  };

  const [birimDonusumleri, setBirimDonusumleri] = useState([]);

  const fetchMalzemeler = async () => {
    try {
      const res = await malzemelerAPI.getAll();
      setMalzemeler(res.data);
    } catch {}
  };

  const fetchBirimDonusumleri = async () => {
    try {
      const res = await malzemeBirimDonusumleriAPI.getAllSon();
      setBirimDonusumleri(res.data);
    } catch {}
  };

  useEffect(() => {
    fetchData();
    fetchMalzemeler();
    fetchBirimDonusumleri();
  }, []);

  // Reçete kalemini malzemenin asıl birimine dönüştür
  const donusturKalem = (k, porsiyon) => {
    const m = malzemeler.find(mal => mal.id === k.malzeme_id);
    if (!m) return { malzeme_id: k.malzeme_id, miktar: k.miktar / porsiyon, birim_id: k.birim_id };

    const secilenBirimId = k.birim_id;
    const malzemeBirimId = m.birim_id;
    const grup = birimGruplari.find(g => g.includes(malzemeBirimId));
    const grupBirimIds = grup ? grup : [malzemeBirimId];

    // Seçilen birim zaten malzemenin grubunda mı?
    if (grupBirimIds.includes(secilenBirimId)) {
      return {
        malzeme_id: k.malzeme_id,
        miktar: k.miktar / porsiyon,
        birim_id: secilenBirimId,
      };
    }

    // Sabit dönüşüm bilgisi
    const adById = Object.fromEntries(birimlerRaw.map(b => [b.id, b.ad]));
    const sabitCarpan = (fromId, toId) => {
      if (fromId === toId) return 1;
      const f = adById[fromId], t = adById[toId];
      if ((f === 'gr' && t === 'kg') || (f === 'ml' && t === 'lt')) return 0.001;
      if ((f === 'kg' && t === 'gr') || (f === 'lt' && t === 'ml')) return 1000;
      return null;
    };

    // Dönüşüm tablosundan çevir
    // Önce doğrudan eşleşme ara (kaynak=seçilen birim)
    let donusum = birimDonusumleri.find(
      d => d.malzeme_id === k.malzeme_id && d.kaynak_birim_id === secilenBirimId
    );
    let onCarpan = 1; // seçilen birimden dönüşüm kaynağına çevirme çarpanı

    // Doğrudan yoksa, seçilen birimin grubundaki diğer birimle ara
    // Ör: ml seçildi, dönüşüm tablosunda lt→gr var → önce ml→lt (÷1000), sonra lt→gr
    if (!donusum) {
      const secilenGrup = birimGruplari.find(g => g.includes(secilenBirimId));
      if (secilenGrup) {
        for (const grupBirimId of secilenGrup) {
          if (grupBirimId === secilenBirimId) continue;
          donusum = birimDonusumleri.find(
            d => d.malzeme_id === k.malzeme_id && d.kaynak_birim_id === grupBirimId
          );
          if (donusum) {
            onCarpan = sabitCarpan(secilenBirimId, grupBirimId);
            break;
          }
        }
      }
    }

    if (donusum && grupBirimIds.includes(donusum.hedef_birim_id)) {
      const hedefBirimId = donusum.hedef_birim_id;
      const donusturulmusMiktar = k.miktar * onCarpan * donusum.carpan;
      // Hedef birim ile malzeme birimi arası sabit dönüşüm (ör: gr → kg)
      let asılMiktar = donusturulmusMiktar;
      let asılBirimId = hedefBirimId;
      const sonCarpan = sabitCarpan(hedefBirimId, malzemeBirimId);
      if (sonCarpan && hedefBirimId !== malzemeBirimId) {
        asılMiktar = donusturulmusMiktar * sonCarpan;
        asılBirimId = malzemeBirimId;
      }
      return {
        malzeme_id: k.malzeme_id,
        miktar: asılMiktar / porsiyon,
        birim_id: asılBirimId,
        gosterim_miktar: k.miktar / porsiyon,
        gosterim_birim_id: secilenBirimId,
      };
    }

    // Dönüşüm bulunamadıysa olduğu gibi kaydet
    return {
      malzeme_id: k.malzeme_id,
      miktar: k.miktar / porsiyon,
      birim_id: secilenBirimId,
    };
  };

  const handleSubmit = async (values) => {
    try {
      const { receteKalemleri, ...urunValues } = values;

      if (editingRecord) {
        await urunlerAPI.update(editingRecord.id, urunValues);
        message.success('Ürün güncellendi');
      } else {
        const urunRes = await urunlerAPI.create(urunValues);
        const urunId = urunRes.data.id;

        if (receteKalemleri && receteKalemleri.length > 0) {
          const porsiyon = values.recetePorsiyon || 1;
          await urunRecetelerAPI.create({
            urun_id: urunId,
            aciklama: urunValues.urun_adi + ' reçetesi',
            porsiyon,
            kalemler: receteKalemleri.map(k => donusturKalem(k, porsiyon)),
          });
        }
        message.success('Ürün ve reçete eklendi');
      }

      setModalOpen(false);
      setEditingRecord(null);
      form.resetFields();
      fetchData();
    } catch (err) {
      message.error((editingRecord ? 'Ürün güncellenemedi: ' : 'Ürün eklenemedi: ') + (err.response?.data?.hata || err.message));
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    form.setFieldsValue({
      urun_adi: record.urun_adi,
      aciklama: record.aciklama,
      urun_tipi_id: record.urun_tipi_id,
      birim_id: record.birim_id,
      satis_fiyati: record.satis_fiyati,
    });
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await urunlerAPI.remove(id);
      message.success('Ürün silindi');
      fetchData();
    } catch (err) {
      message.error('Ürün silinemedi: ' + (err.response?.data?.hata || err.message));
    }
  };

  const handleReceteGor = (urun) => {
    setSelectedUrun(urun);
    setReceteModalOpen(true);
  };

  const malzemeOptions = malzemeler.map(m => ({ value: m.id, label: m.ad }));

  // Birim dönüşüm grupları: aynı gruptakiler arası seçim yapılabilir (DB'den ad ile eşleşir)
  const idByAd = Object.fromEntries(birimlerRaw.map(b => [b.ad, b.id]));
  const birimGruplari = [
    [idByAd['kg'], idByAd['gr']].filter(Boolean),
    [idByAd['lt'], idByAd['ml']].filter(Boolean),
  ];

  const getSecilebilenBirimler = (malzemeId) => {
    const m = malzemeler.find(mal => mal.id === malzemeId);
    if (!m) return [];
    // Sabit grup (kg/gr, lt/ml)
    const grup = birimGruplari.find(g => g.includes(m.birim_id));
    const grupBirimIds = grup ? [...grup] : [m.birim_id];
    // Dönüşüm tablosundan ek birimler + hedef birimin grubu
    const donusumEkIds = [];
    birimDonusumleri
      .filter(d => d.malzeme_id === malzemeId)
      .forEach(d => {
        donusumEkIds.push(d.kaynak_birim_id);
        // Kaynak birimin grubunu da ekle (lt→ml, kg→gr vs.)
        const kaynakGrup = birimGruplari.find(g => g.includes(d.kaynak_birim_id));
        if (kaynakGrup) kaynakGrup.forEach(id => donusumEkIds.push(id));
        // Hedef birimin grubunu da ekle (gr→kg, lt→ml vs.)
        const hedefGrup = birimGruplari.find(g => g.includes(d.hedef_birim_id));
        if (hedefGrup) hedefGrup.forEach(id => donusumEkIds.push(id));
      });
    const tumIds = [...new Set([...grupBirimIds, ...donusumEkIds])];
    return birimler.filter(b => tumIds.includes(b.value));
  };

  const handleMalzemeChange = (malzemeId, formInstance, fieldName) => {
    const m = malzemeler.find(mal => mal.id === malzemeId);
    if (m) {
      formInstance.setFieldValue(fieldName, m.birim_id);
    }
  };

  const handleHizliMalzemeEkle = () => {
    malzemeForm.setFieldsValue({ ad: malzemeArama });
    setMalzemeModalOpen(true);
  };

  const handleMalzemeKaydet = async (values) => {
    try {
      await malzemelerAPI.create(values);
      message.success('Malzeme eklendi');
      setMalzemeModalOpen(false);
      malzemeForm.resetFields();
      await fetchMalzemeler();
    } catch (err) {
      message.error('Malzeme eklenemedi: ' + (err.response?.data?.hata || err.message));
    }
  };

  const malzemeNotFound = (
    <div style={{ textAlign: 'center', padding: 8 }}>
      <p style={{ color: '#999', marginBottom: 8 }}>
        "{malzemeArama}" bulunamadı
      </p>
      <Button type="link" onClick={handleHizliMalzemeEkle}>
        Malzeme olarak eklemek için tıklayın
      </Button>
    </div>
  );

  const malzemeSelectProps = {
    placeholder: 'Malzeme',
    options: malzemeOptions,
    style: { width: 200 },
    showSearch: true,
    optionFilterProp: 'label',
    onSearch: (val) => setMalzemeArama(val),
    notFoundContent: malzemeArama ? malzemeNotFound : null,
  };

  const filteredData = useMemo(() => {
    return data.filter(u => {
      if (aramaMetni) {
        const q = aramaMetni.toLowerCase();
        if (!((u.urun_adi || '').toLowerCase().includes(q) || (u.aciklama || '').toLowerCase().includes(q))) return false;
      }
      if (filtreAdIds.length > 0 && !filtreAdIds.includes(u.id)) return false;
      if (!secimeUyuyor(filtreTipIds, u.urun_tipi_id)) return false;
      if (filtreFiyatOp && filtreFiyatDeger != null) {
        const fiyat = Number(u.satis_fiyati || 0), deger = Number(filtreFiyatDeger);
        if (filtreFiyatOp === 'gt' && !(fiyat > deger)) return false;
        if (filtreFiyatOp === 'gte' && !(fiyat >= deger)) return false;
        if (filtreFiyatOp === 'lt' && !(fiyat < deger)) return false;
        if (filtreFiyatOp === 'lte' && !(fiyat <= deger)) return false;
        if (filtreFiyatOp === 'eq' && !(fiyat === deger)) return false;
      }
      if (filtreMaliyetOp && filtreMaliyetDeger != null) {
        const maliyet = Number(u.maliyet || 0), deger = Number(filtreMaliyetDeger);
        if (filtreMaliyetOp === 'gt' && !(maliyet > deger)) return false;
        if (filtreMaliyetOp === 'gte' && !(maliyet >= deger)) return false;
        if (filtreMaliyetOp === 'lt' && !(maliyet < deger)) return false;
        if (filtreMaliyetOp === 'lte' && !(maliyet <= deger)) return false;
        if (filtreMaliyetOp === 'eq' && !(maliyet === deger)) return false;
      }
      return true;
    });
  }, [data, aramaMetni, filtreAdIds, filtreTipIds, filtreFiyatOp, filtreFiyatDeger, filtreMaliyetOp, filtreMaliyetDeger]);

  const urunAdOptions = useMemo(() =>
    [...data].sort((a, b) => (a.urun_adi || '').localeCompare(b.urun_adi || '', 'tr')).map(u => ({ value: u.id, label: u.urun_adi })),
    [data]
  );

  const urunTipiFilterOptions = useMemo(
    () => bosSecenekliOptions(urunTipleri, data, 'urun_tipi_id'),
    [urunTipleri, data]
  );

  const filtreAktifMi = filtreAdIds.length > 0 || filtreTipIds.length > 0 || filtreFiyatOp || filtreMaliyetOp;
  const filtreTemizle = () => { setFiltreAdIds([]); setFiltreTipIds([]); setFiltreFiyatOp(null); setFiltreFiyatDeger(null); setFiltreMaliyetOp(null); setFiltreMaliyetDeger(null); };

  const strSorter = (key) => (a, b) => (a[key] || '').localeCompare(b[key] || '', 'tr');
  const numSorter = (key) => (a, b) => Number(a[key] || 0) - Number(b[key] || 0);

  const columns = [
    { title: 'Ürün Adı', dataIndex: 'urun_adi', key: 'urun_adi', sorter: strSorter('urun_adi'),
      render: (text, record) => (
        <a onClick={() => { setDetayUrun(record); setDetayOpen(true); }}>{text}</a>
      ),
    },
    { title: 'Tip', dataIndex: 'tip', key: 'tip', sorter: strSorter('tip'), responsive: ['md'] },
    { title: 'Birim', dataIndex: 'birim', key: 'birim', sorter: strSorter('birim'), responsive: ['sm'] },
    { title: 'Satış Fiyatı', dataIndex: 'satis_fiyati', key: 'satis_fiyati', sorter: numSorter('satis_fiyati'), responsive: ['sm'] },
    { title: 'Maliyet', dataIndex: 'maliyet', key: 'maliyet', sorter: numSorter('maliyet'), width: isMobile ? 65 : undefined, render: (val) => val != null ? formatPara(val) : <span style={{ color: '#999' }}>-</span> },
    { title: 'Stok', dataIndex: 'stok_miktari', key: 'stok_miktari', sorter: numSorter('stok_miktari'), width: isMobile ? 45 : undefined },
    {
      title: 'İşlem', key: 'islem',
      render: (_, record) => (
        <Space size={4}>
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            {!isMobile && 'Düzenle'}
          </Button>
          <Button size="small" icon={<EyeOutlined />} onClick={() => handleReceteGor(record)}>
            {!isMobile && 'Reçete'}
          </Button>
          <Popconfirm
            title="Ürünü Sil"
            description={`"${record.urun_adi}" ürünü silinecek. Satış/menü/stok/üretim kaydı varsa engellenir. Reçetesi de silinir.`}
            onConfirm={() => handleDelete(record.id)}
            okText="Evet, sil"
            cancelText="Vazgeç"
            okButtonProps={{ danger: true }}
          >
            <Button danger size="small" icon={<DeleteOutlined />}>
              {!isMobile && 'Sil'}
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  // Gerçek miktar → malzeme birimine dönüştürülmüş maliyet
  const gercekMaliyet = (r) => {
    if (r.malzeme_birim_fiyat == null) return null;
    const fiyat = Number(r.malzeme_birim_fiyat);
    const miktar = Number(r.miktar);
    const receteBirim = r.birim;
    const malzemeBirim = r.malzeme_birim;
    if (receteBirim === malzemeBirim) return miktar * fiyat;
    if ((receteBirim === 'gr' && malzemeBirim === 'kg') || (receteBirim === 'ml' && malzemeBirim === 'lt')) return (miktar / 1000) * fiyat;
    if ((receteBirim === 'kg' && malzemeBirim === 'gr') || (receteBirim === 'lt' && malzemeBirim === 'ml')) return (miktar * 1000) * fiyat;
    return miktar * fiyat;
  };

  const receteKalemColumns = [
    { title: 'Malzeme', dataIndex: 'malzeme_adi', key: 'malzeme_adi' },
    { title: 'Miktar', key: 'miktar', render: (_, r) => r.gosterim_miktar ? `${r.gosterim_miktar} ${r.gosterim_birim}` : `${r.miktar} ${r.birim}` },
    { title: 'Gerçek Miktar', key: 'gercek', render: (_, r) => `${r.miktar} ${r.birim}` },
    { title: 'Birim Fiyat', key: 'birim_fiyat', align: 'right',
      render: (_, r) => {
        if (r.malzeme_birim_fiyat == null) return <span style={{ color: '#999' }}>-</span>;
        return `${formatSayi(r.malzeme_birim_fiyat, 2)} ₺/${r.malzeme_birim}`;
      },
    },
    { title: 'Maliyet', key: 'maliyet', align: 'right',
      render: (_, r) => {
        const m = gercekMaliyet(r);
        if (m == null) return <span style={{ color: '#999' }}>-</span>;
        return `${formatSayi(m, 2)} ₺`;
      },
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'nowrap' }}>
        <h2 style={{
          margin: 0,
          fontSize: isMobile ? 16 : 24,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          minWidth: 0,
        }}>Ürünler</h2>
        <Space size={6} style={{ flexShrink: 0 }}>
          <Input
            placeholder="Ara..."
            prefix={<SearchOutlined />}
            allowClear
            onChange={(e) => setAramaMetni(e.target.value)}
            style={{ width: isMobile ? 110 : 200 }}
          />
          <Button
            icon={<FilterOutlined />}
            onClick={() => setFiltreAcik(filtreAcik.length ? [] : ['1'])}
            type={filtreAktifMi ? 'primary' : 'default'}
            ghost={filtreAktifMi}
            title={filtreAktifMi ? 'Filtreler (Aktif)' : 'Filtreler'}
          >
            {!isMobile && `Filtreler${filtreAktifMi ? ' (Aktif)' : ''}`}
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setModalOpen(true)}
            title="Yeni Ürün"
          >
            {!isMobile && 'Yeni Ürün'}
          </Button>
        </Space>
      </div>

      <Collapse activeKey={filtreAcik} onChange={setFiltreAcik} ghost items={[{ key: '1', showArrow: false, header: null, children: (
        <Card size="small" style={{ marginBottom: 16 }} title={<span><FilterOutlined /> Akıllı Filtreler</span>} extra={filtreAktifMi && <Button size="small" icon={<ClearOutlined />} onClick={filtreTemizle}>Temizle</Button>}>
          <Row gutter={[16, 12]} wrap={false}>
            <Col flex="1 1 0" style={{ minWidth: 0 }}>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Ürün Adı</label>
              <Select mode="multiple" style={{ width: '100%' }} placeholder="Ürün seçin..." options={urunAdOptions} value={filtreAdIds} onChange={setFiltreAdIds} showSearch optionFilterProp="label" maxTagCount={1} allowClear />
            </Col>
            <Col flex="1 1 0" style={{ minWidth: 0 }}>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Ürün Tipi</label>
              <Select mode="multiple" style={{ width: '100%' }} placeholder="Tip seçin..." options={urunTipiFilterOptions} value={filtreTipIds} onChange={setFiltreTipIds} maxTagCount={1} allowClear />
            </Col>
            <Col flex="1 1 0" style={{ minWidth: 0 }}>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Satış Fiyatı</label>
              <Space.Compact style={{ width: '100%' }}>
                <Select style={{ width: '45%' }} placeholder="Koşul" value={filtreFiyatOp} onChange={setFiltreFiyatOp} allowClear options={[{ value: 'gt', label: '>' }, { value: 'gte', label: '>=' }, { value: 'lt', label: '<' }, { value: 'lte', label: '<=' }, { value: 'eq', label: '=' }]} />
                <InputNumber {...paraInputProps} style={{ width: '55%' }} placeholder="Fiyat" value={filtreFiyatDeger} onChange={setFiltreFiyatDeger} min={0} />
              </Space.Compact>
            </Col>
            <Col flex="1 1 0" style={{ minWidth: 0 }}>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Maliyet</label>
              <Space.Compact style={{ width: '100%' }}>
                <Select style={{ width: '45%' }} placeholder="Koşul" value={filtreMaliyetOp} onChange={setFiltreMaliyetOp} allowClear options={[{ value: 'gt', label: '>' }, { value: 'gte', label: '>=' }, { value: 'lt', label: '<' }, { value: 'lte', label: '<=' }, { value: 'eq', label: '=' }]} />
                <InputNumber {...paraInputProps} style={{ width: '55%' }} placeholder="Maliyet" value={filtreMaliyetDeger} onChange={setFiltreMaliyetDeger} min={0} />
              </Space.Compact>
            </Col>
          </Row>
        </Card>
      )}]} />

      {filtreAktifMi && (
        <Card size="small" style={{ marginBottom: 16, background: '#fafafa' }}>
          <Row gutter={16}>
            <Col xs={12} sm={8} md={6}><Statistic title="Ürün Sayısı" value={filteredData.length} /></Col>
          </Row>
        </Card>
      )}

      <Table columns={columns} dataSource={filteredData} rowKey="id" loading={loading} pagination={{ defaultPageSize: 10, pageSizeOptions: ['10', '20', '50'], showSizeChanger: true, showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} kayıt` }} />

      {/* Yeni Ürün + Reçete Modalı */}
      <Modal
        title={editingRecord ? 'Ürün Düzenle' : 'Yeni Ürün'}
        open={modalOpen}
        onCancel={() => { setModalOpen(false); setEditingRecord(null); form.resetFields(); }}
        onOk={() => form.submit()}
        width={isMobile ? '95vw' : 700}
        style={isMobile ? { top: 10, paddingBottom: 0 } : undefined}
        styles={isMobile ? { body: { maxHeight: 'calc(100vh - 120px)', overflowY: 'auto', padding: 12 } } : undefined}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="urun_adi" label="Ürün Adı" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="aciklama" label="Açıklama">
            <Input />
          </Form.Item>
          <Form.Item name="urun_tipi_id" label="Tip">
            <Select options={urunTipleri} onChange={async (val) => {
              if (!editingRecord && val) {
                try {
                  const res = await listeFiyatlariAPI.getByTip('urun', val);
                  if (res.data) {
                    form.setFieldsValue({ satis_fiyati: res.data.fiyat });
                  }
                } catch {}
              }
            }} />
          </Form.Item>
          <Form.Item name="birim_id" hidden initialValue={porsiyonBirimId}>
            <InputNumber />
          </Form.Item>
          <Form.Item label="Birim">
            <Input value="porsiyon" disabled />
          </Form.Item>
          <Form.Item name="satis_fiyati" label="Satış Fiyatı">
            <InputNumber {...paraInputProps} style={{ width: '100%' }} />
          </Form.Item>

          <Divider>Reçete</Divider>

          <Form.Item name="recetePorsiyon" label="Kaç porsiyon için malzeme giriyorsunuz?" initialValue={1} extra="Girdiğiniz miktarlar bu sayıya bölünerek 1 porsiyonluk reçete oluşturulur.">
            <InputNumber min={1} style={{ width: '100%' }} placeholder="Örn: 50" />
          </Form.Item>

          <Form.List name="receteKalemleri">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                    <Form.Item {...restField} name={[name, 'malzeme_id']} rules={[{ required: true, message: 'Malzeme seçin' }]}>
                      <Select {...malzemeSelectProps} onChange={(val) => handleMalzemeChange(val, form, ['receteKalemleri', name, 'birim_id'])} />
                    </Form.Item>
                    <Form.Item {...restField} name={[name, 'miktar']} rules={[{ required: true, message: 'Miktar girin' }]}>
                      <InputNumber placeholder="Miktar" min={0.001} step={0.1} precision={3} />
                    </Form.Item>
                    <Form.Item noStyle dependencies={[['receteKalemleri', name, 'malzeme_id']]}>
                      {() => {
                        const mid = form.getFieldValue(['receteKalemleri', name, 'malzeme_id']);
                        const opts = getSecilebilenBirimler(mid);
                        const m = malzemeler.find(mal => mal.id === mid);
                        return (
                          <Space.Compact>
                            <Form.Item {...restField} name={[name, 'birim_id']} rules={[{ required: true, message: 'Birim seçin' }]} noStyle>
                              <Select style={{ width: 90 }} options={opts} disabled={opts.length <= 1} />
                            </Form.Item>
                            {m && (
                              <Button icon={<SwapOutlined />} title="Birim dönüşümü ekle"
                                onClick={() => { setDonusumMalzeme(m); setDonusumOpen(true); }} />
                            )}
                          </Space.Compact>
                        );
                      }}
                    </Form.Item>
                    <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(name)} />
                  </Space>
                ))}
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                  Malzeme Ekle
                </Button>
              </>
            )}
          </Form.List>
        </Form>
      </Modal>

      <ReceteModal
        open={receteModalOpen}
        urun={selectedUrun}
        onClose={() => { setReceteModalOpen(false); setSelectedUrun(null); }}
        onSaved={fetchData}
      />

      {/* Hızlı Malzeme Ekleme Modalı */}
      <Modal
        title="Hızlı Malzeme Ekle"
        open={malzemeModalOpen}
        onCancel={() => { setMalzemeModalOpen(false); malzemeForm.resetFields(); }}
        onOk={() => malzemeForm.submit()}
        zIndex={1100}
      >
        <Form form={malzemeForm} layout="vertical" onFinish={handleMalzemeKaydet}>
          <Form.Item name="ad" label="Ad" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="marka" label="Marka">
            <Input />
          </Form.Item>
          <Form.Item name="aciklama" label="Açıklama">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="malzeme_tipi_id" label="Tip">
            <Select options={malzemeTipleri} />
          </Form.Item>
          <Form.Item name="birim_id" label="Birim">
            <Select options={birimler} />
          </Form.Item>
          <Form.Item name="kritik_seviye" label="Kritik Seviye">
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      <UrunDetayModal
        open={detayOpen}
        urun={detayUrun}
        onClose={() => { setDetayOpen(false); setDetayUrun(null); }}
        onDelete={async (id) => {
          await handleDelete(id);
          setDetayOpen(false);
          setDetayUrun(null);
        }}
      />

      <BirimDonusumModal
        open={donusumOpen}
        malzeme={donusumMalzeme}
        birimler={birimler}
        birimlerRaw={birimlerRaw}
        onClose={() => { setDonusumOpen(false); setDonusumMalzeme(null); }}
        onSaved={() => fetchBirimDonusumleri()}
      />
    </div>
  );
}

export default Urunler;